/**
 * Distance -> gain mapping.
 *
 * On a real theremin the volume loop *mutes* as the hand approaches, so the default here is
 * "far = loud". The perceptual curve is squared, which is a cheap approximation of the
 * loudness curve and keeps the quiet end of the range controllable.
 */

import { clamp, clamp01, normalizeRange } from '../tracking/types';
import type { DistanceMappingOptions } from './pitchMapping';

/** Hard ceiling on the pre-limiter signal so the chain can never blow up. */
export const MAX_OUTPUT_GAIN = 0.85;

/**
 * Convert a hand-to-loop distance (isotropic screen units) into a normalized control value
 * where **1 means "far from the loop" = loud**.
 */
export function distanceToVolumeNormalized(
  distance: number,
  options: DistanceMappingOptions,
): number {
  const near = Math.max(0, options.near);
  const sensitivity = clamp(options.sensitivity, 0.25, 4);
  const span = Math.max(1e-4, (options.far - near) / sensitivity);
  const raw = normalizeRange(distance, near, near + span);
  return options.invert ? 1 - raw : raw;
}

/**
 * Perceptual curve applied to the normalized volume control.
 * `gain = normalized^2`, then scaled by master volume and clamped to the output ceiling.
 */
export function normalizedToGain(normalized: number, masterVolume: number): number {
  const n = clamp01(normalized);
  const curved = n * n;
  return clamp(curved * clamp01(masterVolume) * MAX_OUTPUT_GAIN, 0, MAX_OUTPUT_GAIN);
}

/** Inverse of the curve (without master scaling), for UI readouts and tests. */
export function gainToNormalized(gain: number, masterVolume: number): number {
  const master = clamp01(masterVolume) * MAX_OUTPUT_GAIN;
  if (master <= 1e-6) return 0;
  return clamp01(Math.sqrt(clamp01(gain / master)));
}

/** Linear gain -> decibels, floored so silence does not produce -Infinity. */
export function gainToDecibels(gain: number, floorDb = -90): number {
  if (gain <= 1e-6) return floorDb;
  return Math.max(floorDb, 20 * Math.log10(gain));
}
